"use client"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import Banner from "../shared/Banner"
import BannerPlaceholder from "../shared/BannerPlaceholder"
import MediaCenterEvents from "./MediaCenterEvents"
import CustomButton from "../shared/CustomButton"
import ArrowDown from "../shared/icons/ArrowDown"
import Popup from "../shared/Popup/Popup"
import MediaCenterDetails from "./MediaCenterDetails"
import useMediaGetter from "@/app/hooks/useMediaGetter"
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react"
import { FreeMode, Navigation } from "swiper/modules"
import ImageWithLoader from "../shared/imageWithLoader/ImageWithLoader";
import { TBannerResponse } from "@/app/types/Shared/BannerAndShortVideo"
import { IMediaCenterProps } from "./types"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import FiltrationComp from "../shared/FiltrationComp"
import { screenSizeContext } from "@/app/store/context/screenSizesContext"
import { loadingContext } from "@/app/store/context/isLoadingContext"
import { usePopupContext } from "@/app/store/context/popupContext"
import CloseWidthImage from "../shared/icons/CloseWidthImage"
import CircleIconButton from "../shared/CircleIconButton"
import OneDirectionArrow from "../shared/icons/OneDirectionArrow"
import dynamic from 'next/dynamic';
import gsap from "gsap"

const ReactPlayerComp = dynamic(() => import('react-player'), {
	ssr: false,
});

interface TOptions {
	label: string
	value: string
}
const MediaCenterComponent = ({
	mediaData,
	banner,
	initialBannerImageSrc,
}: {
	mediaData: any
	banner: TBannerResponse
	initialBannerImageSrc?: string
}) => {
	const { isIpad } = useContext(screenSizeContext);
	const { isPageFetched, setIsPageFetched } = useContext(loadingContext)

	const sortedArticles = useMemo(
		() =>
			[...(mediaData?.articles ?? [])].sort(
				(a: { pivot: { order: number } }, b: { pivot: { order: number } }) =>
					a?.pivot?.order - b?.pivot?.order
			),
		[mediaData?.articles]
	)

	const dateOptions = [
		{
			label: "Oldest",
			value: "oldest",
		},
		{
			label: "Latest",
			value: "latest",
		},
	]

	const neighborOptions: TOptions[] = useMemo(
		() =>
			[
				...new Map<string, TOptions>(
					sortedArticles.map((item: any) => [
						item.slug_label,
						{ label: item.slug_label, value: item.slug_label },
					])
				).values(),
			],
		[sortedArticles]
	)

	const [selectedType, setSelectedType] = useState("")
	const [selectedDate, setSelectedDate] = useState("")

	const filteredData = useMemo(() => {
		let finalData = [...sortedArticles]
		if (selectedDate == "latest") {
			finalData = finalData.sort(
				(a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime()
			)
		} else {
			finalData = finalData.sort(
				(a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()
			)
		}
		if (selectedType) {
			finalData = finalData.filter((item) => item.slug_label == selectedType)
		}
		return finalData
	}, [sortedArticles, selectedDate, selectedType])

	const [cardsToShow, setCardsToShow] = useState(5)
	const handleLoadMore = () => {
		setCardsToShow(cardsToShow + 3)
	}
	const [activeModal, setActiveModal] = useState<boolean>(false)
	const [modalData, setModalData] = useState<IMediaCenterProps | null>(null)

	const { imgSrc } = useMediaGetter(banner?.media)
	const bannerImageSrc = imgSrc ?? initialBannerImageSrc


	const params = useSearchParams()
	const pathname = usePathname()
	const { replace } = useRouter()

	useEffect(() => {
		if (mediaData) {
			const slug = params.get("slug")
			if (slug) {
				const article = mediaData?.articles?.find((item: any) => item.url_slug == slug)
				if (article) {
					setisOtherPopupOpen(true);
					setPopupInnerData(article);
				}
			}
		}
	}, [mediaData, params])

	useEffect(() => {
		if (bannerImageSrc) {
			setTimeout(() => {
				setIsPageFetched((prev: string) => {
					if (prev === "loading") return "fetched";
					return prev;
				})
			})
		}
	}, [bannerImageSrc, setIsPageFetched])

	const tl = useRef<GSAPTimeline>(gsap.timeline());
	useEffect(() => {
		if (isPageFetched === "fetched" && bannerImageSrc) {
			const timer = setTimeout(()=> {
				const html = document.documentElement;

				tl.current
				.timeScale(html.classList.contains("loaded") ? 1.25 : 1)
				.set(".banner .hero-title-word", { yPercent: 50, alpha: 0 })
				.to(".banner .hero-image img", {
					delay: html.classList.contains("loaded") ? 0.8 : 2.2,
					duration: 1,
					ease: "power2.inOut",
					y: "0%"
				})
				.to(".banner .hero-image-wrapper", {
					duration: 1,
					ease: "power2.inOut",
					scale: 1,
				}, "-=0.25")
				.to(".navbar_section", {
					duration: 1,
					ease: "power2.inOut",
					y: "0%"
				}, "-=0.4")
				.to(".banner .hero-title-word", {
					duration: 1,
					stagger: 0.05,
					ease: "expo.inOut",
					yPercent: 0,
					alpha: 1
				}, "<");
			}, 50);

			return () => clearTimeout(timer);
		}
	}, [isPageFetched, bannerImageSrc]);

    const {
        isOpen,
        isOtherPopupOpen,
        images,
        selectedImageIndex,
        closePopup,
        popupInnerData,
        setisOtherPopupOpen,
        setPopupInnerData
    } = usePopupContext()

	const fullScreenSwiperRef = useRef<SwiperRef>(null)
	const playersRef = useRef<any[]>([]);
	const [playingIndex, setPlayingIndex] = useState<number | null>(null);

	const [isFirstSlide, setIsFirstSlide] = useState(true)
	const [isLastSlide, setIsLastSlide] = useState(false)

	const handleSlideChange = (swiper: any) => {
		// Update isFirstSlide and isLastSlide using fullScreenSwiperRef
		if (fullScreenSwiperRef?.current) {
			const swiperInstance = fullScreenSwiperRef.current.swiper;
			setIsFirstSlide(swiperInstance?.isBeginning);
			setIsLastSlide(swiperInstance?.isEnd);
		}

		// Pause and reset all video players
		playersRef.current.forEach((player, i) => {
			if (player) {
				try {
					const internalPlayer = player.getInternalPlayer?.();
					internalPlayer?.pause?.(); // Try pausing the internal HTML5 video
					player.seekTo?.(0); // Reset to beginning
				} catch (e) {
					console.warn(`Failed to pause/reset player ${i}:`, e);
				}
			}
		});

		// Slight delay helps ReactPlayer re-sync before updating the index
		setTimeout(() => {
			setPlayingIndex(swiper.activeIndex);
		}, 100);
	};

	return (
		<div className={`page_wrapper media-center-page ${isIpad && "media-page"}`}>
			{bannerImageSrc ? (
				<Banner
					priority
					imageSrc={`${bannerImageSrc}`}
					minHeight="75vh"
					mobileHeight="75vh"
					title={banner?.title?.en}
					noDescriptionWithSmallTitle
					optimizedImage
				/>
			) : (
				<BannerPlaceholder minHeight="75vh" mobileHeight="75vh" />
			)}
			<div className="content_wrapper px pt-11 lg:pt-[6.25rem] lg:pb-0 bg-white min-h-[20rem]">
				<FiltrationComp
					dateOptions={dateOptions}
					neighborOptions={neighborOptions}
					setSelectedDate={setSelectedDate}
					setSelectedTab={setSelectedType}
					placeholderText={"Type"}
				/>
				<MediaCenterEvents
					data={filteredData}
					cardsToShow={cardsToShow}
					setActiveModal={setActiveModal}
					setModalData={setModalData}
					optimizedImage
				/>
				{cardsToShow < filteredData.length && (
					<div className="flex justify-center mb-11 lg:mb-[6.25rem]">
						<CustomButton
							text="LOAD MORE"
							hasIcon
							style="blue"
							customClass="w-fit"
							action={handleLoadMore}
							icon={<ArrowDown color="white" />}
						/>
					</div>
				)}
			</div>
			{/* <Popup
				customClassWrapper="popup_inline  !z-[9988]"
				setIsOpen={setActiveModal}
				isOpen={activeModal}
				onClose={() => {
					const slug = params.get("slug");
					if (slug) {
						const queryParams = new URLSearchParams(params.toString())
						queryParams.delete("slug")
						replace(`${pathname}?${queryParams.toString()}`, { scroll: false })
					}
					setActiveModal(false);
				}}

			>
				<MediaCenterDetails
					data={modalData}
					fullData={mediaData.articles}
					setModalData={setModalData}
					insideAmenitiesDetailsPage
				/>
			</Popup> */}

			<Popup
				customClassWrapper="popup_half z-[9999] overflow-hidden"
				setIsOpen={closePopup}
				isOpen={isOpen}
				onClose={() => {
					setPlayingIndex(null);
					setIsFirstSlide(true);
					setIsLastSlide(false);
				}}
			>
				<div className={`full-screen-swiper-wrapper relative w-full h-full !py-0`}>
					<Swiper
						ref={fullScreenSwiperRef}
						initialSlide={selectedImageIndex} // Start from the selected image
						modules={[Navigation, FreeMode]}
						spaceBetween={0}
						slidesPerView={1}
						onSlideChange={handleSlideChange}
						className="w-full h-full"
						navigation={{
							nextEl: ".swiper-full-next",
							prevEl: ".swiper-full-prev",
						}}
						freeMode
					>
						{images?.map((item, index) => (
							<SwiperSlide key={item?.id || index}>
								<div className="relative w-full h-full bg-black">
									{
										item?.type !== "video" && (
											<ImageWithLoader
												src={item.src}
												alt={item.alt}
												contain
												sizeVariant="modal"
												quality={75}
											/>
										)
									}

									{
										item?.type === "video" && (
											<div className=" h-full relative"
											>
												<ReactPlayerComp
													key={playingIndex === index ? `playing-${index}` : `idle-${index}`} // force refresh
													ref={(el: any) => (playersRef.current[index] = el)}
													url={item?.src}
													playing={playingIndex === index}
													loop
													width="100%"
													height="100%"
													controls={true}
													playsinline
													className="cursor-pointer"
												/>
											</div>
										)
									}
								</div>


							</SwiperSlide>
						))}
					</Swiper>
					<div className="swiper_btns_container absolute w-full flex justify-between top-[50%] translate-y-[-50%] z-[1] px-4
                    pointer-events-none
                    ">
						<div>
							<CircleIconButton
								style="white"
								icon={<OneDirectionArrow color="white" />}
								customClass={`scale-x-[-1] swiper-full-prev ${isFirstSlide
									? "fade-out duration-500 opacity-0"
									: "fade-in duration-500 opacity-100"
									}
                                    pointer-events-auto
                                    `}
							/>
						</div>
						<div>
							<CircleIconButton
								style="white"
								icon={<OneDirectionArrow color="white" />}
								customClass={`swiper-full-next ${isLastSlide
									? "fade-out duration-500 opacity-0"
									: "fade-in duration-500 opacity-100"
									}
                                    pointer-events-auto
                                    `}
							/>
						</div>
					</div>
					<div
						className="absolute right-4 bottom-4 p-4 cursor-pointer "
						style={{
							borderRadius: "8.125rem",
							border: "1px solid rgba(252, 252, 252, 0)",
							background: "rgba(134, 134, 134, 0.5)",
							boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.04)",
							backdropFilter: "blur(32px)",
						}}
						onClick={closePopup}
					>
						<CloseWidthImage />
					</div>
				</div>

			</Popup>

			<Popup
				customClassWrapper="popup_inline !z-[9988]"
				setIsOpen={setisOtherPopupOpen}
				isOpen={isOtherPopupOpen}
				onClose={() => {
					const slug = params.get("slug");
					if (slug) {
						const queryParams = new URLSearchParams(params.toString())
						queryParams.delete("slug")
						replace(`${pathname}?${queryParams.toString()}`, { scroll: false })
					}
					setisOtherPopupOpen(false)
				}}
			>
				<MediaCenterDetails
					data={popupInnerData}
					fullData={mediaData?.articles?.sort((a: { pivot: { order: number } }, b: { pivot: { order: number } }) => a?.pivot?.order - b?.pivot?.order)}
					setModalData={setPopupInnerData}
					optimizedImage
				/>
			</Popup>
		</div>
	)
}

export default MediaCenterComponent
